import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { AlertTriangle, Check, CheckCircle2, Hourglass, Loader2, Minimize2, Paperclip, Wrench, X } from "lucide-react";
import { useCaseGroup, useRealtimeEvent } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { bi, PHASES, type AgentState, type Bi, type Mission, type MissionState } from "../mission/engine";
import { phaseTitle } from "../mission/lead";
import { AgentCard, DocumentSquad, LIVE, StatusChip } from "./AgentCard";
import { ASSISTANT_AGENT_KEY, AskProvider } from "./ask";
import { AskBox, Conversation } from "./Conversation";
import { LeadPanel, type LeadTab } from "./LeadPanel";
import { SourcesStrip } from "./Memory";
import { LogDock } from "./Shell";
import { DocView, WidgetView } from "./Widgets";
import type { DocWidget } from "../mission/widgets";
import { Elapsed, TONE_COLOR, iconOf, useT } from "./kit";

interface StepPayload { runId: string; caseId?: string | null; agentKey: string; step?: { id: string; kind: string; title: string; detail?: string | null } }

/** The running mission: the swarm of agents on the canvas, the lead's column beside it, the beams between them and the event log. */
export function MissionView({ mission, state }: { mission: Mission; state: MissionState }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const swarm = useRef<HTMLDivElement>(null);
  const orb = useRef<HTMLDivElement>(null);
  const anchors = useRef(new Map<string, HTMLElement>());

  // Platform agents that run on this case while the mission is open (dispatched by a wave, or by a workflow step the officer fired) stream
  // their steps onto the canvas: a known run lands on the card that started it, any other gets its own runtime card in the current wave.
  useCaseGroup(state.caseId);
  useRealtimeEvent<StepPayload>("agent.step", (p) => {
    if (!p.step || p.caseId !== state.caseId || p.step.kind === "Token") return;
    // The case assistant answering the officer is the lead's (or a box's) voice: its answer is already in that conversation.
    if (p.agentKey === ASSISTANT_AGENT_KEY) return;
    const known = mission.board.get<string>(`run:${p.runId}`);
    if (known) { mission.runtimeStep(known, p.step); return; }
    // A run a wave dispatched belongs to the card that dispatched it (claimed once, by its first step).
    const owner = mission.board.get<string>(`runtime:${p.agentKey}`);
    if (owner && !mission.board.has(`claimed:${p.agentKey}`)) {
      mission.board.set(`claimed:${p.agentKey}`, p.runId);
      mission.board.set(`run:${p.runId}`, owner);
      mission.runtimeStep(owner, p.step);
      return;
    }
    const id = `runtime-${p.runId}`;
    mission.board.set(`run:${p.runId}`, id);
    const name = mission.board.get<Record<string, Bi>>("agentNames")?.[p.agentKey] ?? bi(humanize(p.agentKey));
    void mission.spawn({
      id, phase: mission.getSnapshot().phase, squad: "runtime", icon: "bot",
      name, role: bi("Platform agent · live from the AI runtime", "وكيل المنصة · مباشر من وقت التشغيل الذكي"),
      run: async (ctx) => {
        ctx.attachRun(p.runId);
        await ctx.think(`${name.en} started on this file in the platform's AI runtime: the workflow dispatched it on the step just taken.`, `بدأ ${name.ar} العمل على هذا الملف في وقت التشغيل الذكي للمنصة: أطلقه سير العمل عند الخطوة التي اتُخذت للتو.`);
        // The run's own Completed step finishes the card; its status is also checked every 10 s in case that event never arrives.
        for (;;) {
          const finished = await Promise.race([ctx.board.need(`runDone:${id}`).then(() => true), ctx.wait(10_000).then(() => false)]);
          if (finished) return;
          const run = await ctx.api.get<{ status: string }>(`/api/ai/runs/${p.runId}`).catch(() => null);
          if (run && ["Succeeded", "Failed", "Cancelled", "Rejected", "TimedOut"].includes(run.status)) {
            mission.runtimeStep(id, { kind: run.status === "Succeeded" ? "Completed" : "Failed", title: run.status === "Succeeded" ? "Completed" : "Stopped" });
            return;
          }
        }
      },
    });
    mission.runtimeStep(id, p.step);
  });

  const viewPhase = state.viewPhase ?? state.phase;
  const agents = useMemo(() => state.order.map((id) => state.agents[id]!).filter((a) => a.phase === viewPhase), [state.order, state.agents, viewPhase]);
  const docs = agents.filter((a) => a.group === "documents");
  const others = agents.filter((a) => a.group !== "documents");
  const live = agents.filter((a) => LIVE.includes(a.status)).length;
  const waiting = agents.filter((a) => a.status === "waiting").length;
  const done = agents.filter((a) => a.status === "done" || a.status === "flagged").length;

  const anchor = useCallback((id: string) => (el: HTMLElement | null) => { if (el) anchors.current.set(id, el); else anchors.current.delete(id); }, []);
  const [leadWide, setLeadWide] = useState(false);
  const [leadTab, setLeadTab] = useState<LeadTab>("conversation");

  return (
    <AskProvider mission={mission}>
    <div ref={container} className="relative flex min-h-0 flex-1 gap-2 p-2">
      <section className="relative z-10 flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
          <h2 className="font-heading text-[15px] font-semibold">
            <span className="text-[color:var(--agx-gold)]">{tr("backoffice:agentic.lead.wave", { n: ["screening", "publication", "review", "assessment", "decision"].indexOf(viewPhase) + 1 })}</span> · {t(phaseTitle(viewPhase))}
          </h2>
          <span className="flex items-center gap-3 text-[11.5px] agx-dim">
            <span>{tr("backoffice:agentic.swarm.agents", { count: agents.length })}</span>
            <span className="flex items-center gap-1" style={{ color: "var(--agx-cyan)" }}><Loader2 className={`size-3 ${live ? "agx-spin" : ""}`} />{tr("backoffice:agentic.swarm.live", { count: live })}</span>
            {waiting ? <span className="flex items-center gap-1" style={{ color: "var(--agx-amber)" }}><AlertTriangle className="size-3" />{tr("backoffice:agentic.swarm.waiting", { count: waiting })}</span> : null}
            <span className="flex items-center gap-1" style={{ color: "var(--agx-green)" }}><CheckCircle2 className="size-3" />{tr("backoffice:agentic.swarm.done", { count: done })}</span>
          </span>
          {state.viewPhase ? <button type="button" className="agx-btn ms-auto h-7 px-2 text-[11px]" onClick={() => mission.setViewPhase(null)}>{tr("backoffice:agentic.swarm.backToCurrent")}</button> : null}
        </div>
        <SourcesStrip state={state} onOpen={setLeadTab} />
        <div ref={swarm} className="@container min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-2 pe-1">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(296px,1fr))] gap-2">
            {docs.length ? <DocumentSquad agents={docs} mission={mission} anchor={anchor("group:documents")} /> : null}
            <AnimatePresence>
              {others.map((a) => <AgentCard key={a.id} agent={a} state={state} mission={mission} anchor={anchor(a.id)} />)}
            </AnimatePresence>
            {!agents.length ? <SpawningPlaceholder /> : null}
          </div>
          {!state.viewPhase ? <EarlierWaves state={state} onOpen={(id) => mission.setFocus(id)} onView={(p) => mission.setViewPhase(p)} /> : null}
        </div>
        <LogDock state={state} />
        <Maximized mission={mission} state={state} anchor={anchor("focus")} />
      </section>
      <div className={`relative z-10 shrink-0 transition-[width] duration-300 max-lg:hidden ${leadWide ? "w-[min(720px,48%)]" : "w-[360px]"}`}>
        <LeadPanel mission={mission} state={state} orbRef={orb} wide={leadWide} onToggleWide={() => setLeadWide((w) => !w)} tab={leadTab} onTab={setLeadTab} />
      </div>
      <Beams container={container} swarm={swarm} orb={orb} anchors={anchors} agents={agents} state={state} />
    </div>
    </AskProvider>
  );
}

/** The waves already concluded, kept on the canvas as one line per agent with what it established. */
function EarlierWaves({ state, onOpen, onView }: { state: MissionState; onOpen: (id: string) => void; onView: (phase: MissionState["phase"]) => void }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const done = PHASES.filter((p) => p !== state.phase && state.phases[p] === "done").reverse();
  if (!done.length) return null;
  return (
    <div className="mt-5 space-y-3">
      {done.map((p) => {
        const agents = state.order.map((id) => state.agents[id]!).filter((a) => a.phase === p);
        const flagged = agents.filter((a) => a.status === "flagged" || a.status === "failed").length;
        return (
          <section key={p} className="agx-glass rounded-md p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <CheckCircle2 className="size-3.5 text-[color:var(--agx-green)]" />
              <button type="button" onClick={() => onView(p)} className="text-[12.5px] font-semibold hover:text-[color:var(--agx-gold)]">
                {tr("backoffice:agentic.lead.wave", { n: PHASES.indexOf(p) + 1 })} · {t(phaseTitle(p))}
              </button>
              <span className="text-[11px] agx-dim">{tr("backoffice:agentic.swarm.agents", { count: agents.length })}{flagged ? ` · ${tr("backoffice:agentic.swarm.flagged", { count: flagged })}` : ""}</span>
            </div>
            <div className="grid gap-1.5 @2xl:grid-cols-2 @5xl:grid-cols-3">
              {agents.map((a) => {
                const Icon = iconOf(a.icon);
                const tone = a.status === "flagged" ? "var(--agx-amber)" : a.status === "failed" ? "var(--agx-rose)" : "var(--agx-green)";
                return (
                  <button key={a.id} type="button" data-squad={a.squad} onClick={() => onOpen(a.id)} className="flex min-w-0 items-center gap-2 rounded-[5px] border border-[color:var(--agx-line)] agx-wash agx-hover px-2 py-1.5 text-start">
                    <span className="agx-orb size-6 shrink-0"><Icon className="size-3" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[11.5px] font-medium">{t(a.name)}</span>
                      <span className="block truncate text-[11px]" style={{ color: tone }}>{a.summary ? t(a.summary) : "—"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

const humanize = (key: string) => key.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

function SpawningPlaceholder() {
  const { t } = useTranslation();
  return (
    <div className="col-span-full grid h-64 place-items-center rounded-md border border-dashed border-[color:var(--agx-line-2)]">
      <p className="agx-shimmer text-[13px]">{t("backoffice:agentic.swarm.assembling")}</p>
    </div>
  );
}

/**
 * Beams from each agent of the wave to the lead's orb, redrawn every other frame without re-rendering React (the paths are set on the
 * elements directly). A finding the lead relays travels along its agent's beam as a glowing packet.
 */
function Beams({ container, swarm, orb, anchors, agents, state }: {
  container: React.RefObject<HTMLDivElement | null>; swarm: React.RefObject<HTMLDivElement | null>; orb: React.RefObject<HTMLDivElement | null>;
  anchors: React.RefObject<Map<string, HTMLElement>>; agents: AgentState[]; state: MissionState;
}) {
  const { dir } = useLocale();
  const paths = useRef(new Map<string, SVGPathElement>());
  const geom = useRef(new Map<string, string>());
  const focus = state.focus;
  const keys = useMemo(() => (focus ? ["focus"] : [...new Set(agents.map((a) => (a.group === "documents" ? "group:documents" : a.id)))]), [agents, focus]);

  useEffect(() => {
    let frame = 0, tick = 0, alive = true;
    const loop = () => {
      if (!alive) return;
      if (tick++ % 2 === 0) {
        const c = container.current?.getBoundingClientRect();
        const o = orb.current?.getBoundingClientRect();
        const s = swarm.current?.getBoundingClientRect();
        if (c && o && s && o.width > 0) {
          const ex = o.left + o.width / 2 - c.left, ey = o.top + o.height / 2 - c.top;
          for (const key of keys) {
            const el = anchors.current?.get(key);
            const path = paths.current.get(key);
            if (!el || !path) continue;
            const r = el.getBoundingClientRect();
            const visible = r.bottom > s.top + 8 && r.top < s.bottom - 8;
            if (!visible) { path.setAttribute("d", ""); geom.current.delete(key); continue; }
            const sx = (dir === "rtl" ? r.left : r.right) - c.left;
            const sy = Math.min(Math.max(r.top + 26, s.top), s.bottom) - c.top;
            const dx = ex - sx;
            const d = `M ${sx} ${sy} C ${sx + dx * 0.45} ${sy}, ${ex - dx * 0.35} ${ey}, ${ex} ${ey}`;
            path.setAttribute("d", d);
            geom.current.set(key, d);
          }
        }
      }
      frame = requestAnimationFrame(loop);
    };
    loop();
    return () => { alive = false; cancelAnimationFrame(frame); };
  }, [keys, container, orb, swarm, anchors, dir]);

  const statusOf = (key: string) => {
    if (key === "focus") key = focus ?? key;
    if (key === "group:documents") {
      const docs = agents.filter((a) => a.group === "documents");
      return docs.some((a) => a.status === "waiting") ? "waiting" : docs.some((a) => LIVE.includes(a.status)) ? "live" : "idle";
    }
    const a = state.agents[key];
    return a?.status === "waiting" ? "waiting" : a && LIVE.includes(a.status) ? "live" : "idle";
  };
  const now = Date.now();
  const packets = state.packets.filter((p) => now - p.at < 1300);

  return (
    <div className="pointer-events-none absolute inset-0 z-20 max-lg:hidden" aria-hidden>
      <svg className="agx-beams h-full w-full">
        {keys.map((key) => {
          const target = key === "focus" ? focus ?? key : key;
          const a = target === "group:documents" ? agents.find((x) => x.group === "documents") : state.agents[target];
          const st = statusOf(key);
          return <path key={key} ref={(el) => { if (el) paths.current.set(key, el); else paths.current.delete(key); }} className="agx-beam" data-squad={a?.squad} data-live={st === "live"} data-status={st === "waiting" ? "waiting" : undefined} />;
        })}
      </svg>
      {packets.map((p) => {
        const agent = state.agents[p.agentId];
        const own = agent?.group === "documents" ? "group:documents" : p.agentId;
        const key = focus ? (own === focus || p.agentId === focus ? "focus" : "") : own;
        const d = geom.current.get(key);
        if (!d) return null;
        return <span key={p.id} className="agx-packet" data-squad={agent?.squad} style={{ offsetPath: `path("${d}")`, background: TONE_COLOR[p.tone] }} />;
      })}
    </div>
  );
}

/**
 * A maximised box: over the canvas, beside the lead's column. An agent shows its whole conversation with a line to ask it something, its
 * result at full size and its findings with their sources; the document squad shows every reader's page and what it found.
 */
function Maximized({ mission, state, anchor }: { mission: Mission; state: MissionState; anchor: (el: HTMLElement | null) => void }) {
  const focus = state.focus;
  useEffect(() => {
    if (!focus) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") mission.setFocus(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focus, mission]);
  const agent = focus && focus !== "group:documents" ? state.agents[focus] : undefined;
  const docs = focus === "group:documents" ? state.order.map((id) => state.agents[id]!).filter((a) => a.group === "documents" && a.phase === (state.viewPhase ?? state.phase)) : [];
  return (
    <AnimatePresence>
      {focus && (agent || docs.length) ? (
        <motion.div key={focus} ref={anchor} className="absolute inset-0 z-30" initial={{ opacity: 0, scale: 0.97, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.22 }}>
          {agent ? <AgentMax agent={agent} state={state} mission={mission} /> : <SquadMax docs={docs} mission={mission} />}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function MaxHeader({ icon: Icon, title, subtitle, live, status, extra, mission }: { icon: React.ComponentType<{ className?: string }>; title: string; subtitle: string; live: boolean; status?: AgentState["status"]; extra?: React.ReactNode; mission: Mission }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 border-b border-[color:var(--agx-line)] p-4">
      <span className="agx-orb size-11 shrink-0" data-live={live} data-status={status}><Icon className="size-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-heading text-lg font-semibold">{title}</p>
        <p className="truncate text-[12px] agx-dim">{subtitle}</p>
      </div>
      {extra}
      <button type="button" className="agx-btn h-8 px-2.5 text-[12px]" onClick={() => mission.setFocus(null)} aria-label={t("backoffice:agentic.restore")}><Minimize2 className="size-3.5" />{t("backoffice:agentic.restore")}</button>
      <button type="button" className="agx-btn size-8 p-0" data-tone="ghost" onClick={() => mission.setFocus(null)} aria-label={t("backoffice:agentic.close")}><X className="size-4" /></button>
    </div>
  );
}

function AgentMax({ agent, state, mission }: { agent: AgentState; state: MissionState; mission: Mission }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const live = LIVE.includes(agent.status);
  return (
    <div className="agx-card h-full" data-squad={agent.squad} data-live={live} data-status={agent.status}>
      <div className="agx-card-inner flex h-full flex-col">
        <MaxHeader icon={iconOf(agent.icon)} title={t(agent.name)} subtitle={t(agent.role)} live={live} status={agent.status} mission={mission}
          extra={<><StatusChip status={agent.status} /><Elapsed from={agent.startedAt} to={agent.endedAt} className="text-[12px] agx-dim" /></>} />
        <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] gap-0 overflow-hidden lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <div className="flex min-h-0 flex-col gap-3 border-[color:var(--agx-line)] p-4 lg:border-e">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] agx-faint">{tr("backoffice:agentic.chat.title")}</p>
            <Conversation agent={agent} state={state} mission={mission} size="full" className="flex-1" />
            <AskBox agentId={agent.id} name={t(agent.name)} size="full" />
          </div>
          <div className="min-h-0 space-y-5 overflow-y-auto p-5">
            {agent.summary ? <p className="rounded-[5px] px-3 py-2 text-[13px] font-semibold" style={{ color: agent.status === "done" ? "var(--agx-green)" : agent.status === "failed" ? "var(--agx-rose)" : "var(--agx-amber)", background: `color-mix(in oklab, ${agent.status === "done" ? "var(--agx-green)" : "var(--agx-amber)"} 10%, transparent)` }}>{t(agent.summary)}</p> : null}
            <WidgetView widget={agent.widget} size="focus" />
            {agent.findings.length ? (
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] agx-faint">{tr("backoffice:agentic.focus.findings")}</p>
                <ul className="space-y-2">
                  {agent.findings.map((f) => (
                    <li key={f.id} className="rounded-[5px] border border-[color:var(--agx-line)] agx-wash p-3 text-[13px] leading-[1.55]" style={{ borderInlineStartColor: TONE_COLOR[f.tone], borderInlineStartWidth: 2 }}>
                      {t(f.text)}
                      {f.source ? <span className="mt-1 flex items-center gap-1 text-[11px] agx-faint"><Paperclip className="size-3" />{t(f.source)}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-4 text-[11.5px] agx-dim">
              <span className="flex items-center gap-1"><Wrench className="size-3.5" />{tr("backoffice:agentic.focus.tools", { count: agent.tools.length })}</span>
              <span className="flex items-center gap-1"><Paperclip className="size-3.5" />{tr("backoffice:agentic.focus.sources", { count: agent.sources })}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SquadMax({ docs, mission }: { docs: AgentState[]; mission: Mission }) {
  const t = useT();
  const { t: tr } = useTranslation();
  const live = docs.some((a) => LIVE.includes(a.status));
  return (
    <div className="agx-card h-full" data-squad="documents" data-live={live}>
      <div className="agx-card-inner flex h-full flex-col">
        <MaxHeader icon={iconOf("file-search")} title={tr("backoffice:agentic.squad.documents")} subtitle={tr("backoffice:agentic.squad.documentsRole", { count: docs.length })} live={live} mission={mission} />
        <div className="grid min-h-0 flex-1 auto-rows-min gap-3 overflow-y-auto p-4 md:grid-cols-2 2xl:grid-cols-3">
          {docs.map((a) => {
            const w = a.widget?.kind === "doc" ? (a.widget as DocWidget) : null;
            const last = a.findings[a.findings.length - 1];
            return (
              <button key={a.id} type="button" onClick={() => mission.setFocus(a.id)} className="flex min-w-0 flex-col gap-2 rounded-md border border-[color:var(--agx-line)] agx-wash agx-hover p-3 text-start" data-squad="documents">
                <div className="h-28">{w ? <DocView w={w} size="card" /> : <div className="agx-page h-full" />}</div>
                <p className="truncate text-[13px] font-semibold" dir="ltr">{a.name.en}</p>
                <p className="flex min-w-0 items-center gap-1.5 text-[12px]">
                  {a.status === "done" ? <Check className="size-3.5 shrink-0" style={{ color: "var(--agx-green)" }} /> : a.status === "flagged" ? <AlertTriangle className="size-3.5 shrink-0" style={{ color: "var(--agx-amber)" }} /> : LIVE.includes(a.status) ? <Loader2 className="size-3.5 shrink-0 agx-spin" style={{ color: "var(--sq)" }} /> : <Hourglass className="size-3.5 shrink-0 agx-faint" />}
                  <span className="truncate agx-dim">{a.summary ? t(a.summary) : t(a.role)}</span>
                </p>
                {last ? <p className="line-clamp-2 text-[12px] leading-[1.45]" style={{ color: TONE_COLOR[last.tone] }}>{t(last.text)}</p> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
